// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { append, patch, updateItem } from '@ngxs/store/operators';

// Services
import { CompanyService } from '../services/company.service';
import { CoreService } from '../../../shared/services/core.service';
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';

// Models
import { CompanyListModel } from '../models/company-view-model.model';
import { AppCleanup } from '../../../shared/models';

// Store
import { CompanyActions } from './company.action';

// Enums
import { StatusType } from '../../../shared/enums';

export interface CompanyStateModel {
	items: CompanyListModel[];
	loaded: boolean;
}

const defaultState: CompanyStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'company',
	defaults: defaultState,
})
@Injectable()
export class CompanyState extends AppCleanup {
	constructor(
		private companyService: CompanyService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	@Action(CompanyActions.GetAll)
	getAll(ctx: StateContext<CompanyStateModel>, action: CompanyActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.companyService
			.getCompanies()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (companies) => {
					ctx.setState({
						items: companies,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.COMPANY.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CompanyActions.SetAll)
	setAll(ctx: StateContext<CompanyStateModel>, action: CompanyActions.SetAll) {
		ctx.setState({
			items: action.companies,
			loaded: true,
		});
	}

	@Action(CompanyActions.Create)
	create(ctx: StateContext<CompanyStateModel>, action: CompanyActions.Create) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.companyService
			.createCompany(action.data.company)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (company) => {
					let newCompany = <CompanyListModel>{
						id: company.id,
						name: company.name,
						isAdmin: false,
						isActive: company.isActive,
						isApproved: false,
						userCount: 0,
						jobCount: 0,
						pendingUsersCount: 0,
					};

					ctx.setState(
						patch({
							items: append([newCompany]),
						})
					);

					ctx.dispatch(new CompanyActions.CreateSuccess(newCompany));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.COMPANY.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CompanyActions.CreateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.COMPANY.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(CompanyActions.Update)
	update(ctx: StateContext<CompanyStateModel>, action: CompanyActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.companyService
			.updateCompany(action.data.company)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (company) => {
					let updatedCompany = <CompanyListModel>{
						id: company.id,
						name: company.name,
						isAdmin: false,
						isActive: company.isActive,
						isApproved: false,
						userCount: 0,
						jobCount: 0,
						pendingUsersCount: 0,
					};

					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === company.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((company) => company.id === action.data.company.id, updatedCompany),
							})
						);
					}

					ctx.dispatch(new CompanyActions.UpdateSuccess(updatedCompany));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.COMPANY.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new CompanyActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.COMPANY.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Selector()
	static isLoaded(state: CompanyStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static companies(state: CompanyStateModel): CompanyListModel[] {
		return state.items;
	}
}
